import { ref, watch } from "vue";
import { debounce } from "es-toolkit";
/**
 * 중복 검증 composable
 * - 입력값 변경 시 debounced API 호출
 * - isDuplicate 상태에 따라 input.rules 배열에 에러 룰 추가/제거
 */
export function useDuplicateCheck(checkFn, errorMsg, debounceMs = 500) {
    const isDuplicate = ref(false);
    const isChecking = ref(false);
    const duplicateErrorRule = () => errorMsg;
    const debouncedCheck = debounce(async (value, excludeId) => {
        if (!value) {
            isDuplicate.value = false;
            isChecking.value = false;
            return;
        }
        try {
            isDuplicate.value = await checkFn(value, excludeId);
        }
        catch {
            isDuplicate.value = false;
        }
        finally {
            isChecking.value = false;
        }
    }, debounceMs);
    const check = (value, excludeId) => {
        isChecking.value = true;
        void debouncedCheck(value, excludeId);
    };
    /**
     * input.rules 배열에 중복 에러 룰을 추가/제거한다.
     * 배열을 새로 교체하여 Vuetify 2의 deep watch가 변경을 감지하도록 한다.
     */
    const updateInputRules = (input) => {
        if (!input?.rules)
            return;
        const idx = input.rules.indexOf(duplicateErrorRule);
        if (isDuplicate.value && idx === -1) {
            input.rules = [...input.rules, duplicateErrorRule];
        }
        else if (!isDuplicate.value && idx !== -1) {
            input.rules = input.rules.filter((r) => r !== duplicateErrorRule);
        }
    };
    const bindToInput = (inputRef, getExcludeId) => {
        watch(() => inputRef.value?.value, (newValue) => {
            if (typeof newValue === "string") {
                check(newValue, getExcludeId?.());
            }
        });
        watch(isDuplicate, () => {
            updateInputRules(inputRef.value);
        });
    };
    const reset = () => {
        debouncedCheck.cancel();
        isDuplicate.value = false;
        isChecking.value = false;
    };
    return {
        isDuplicate,
        isChecking,
        check,
        updateInputRules,
        bindToInput,
        reset,
    };
}
